import * as React from "react";
import Box from "@mui/material/Box";
import Autocomplete from "@mui/material/Autocomplete";
// import { TextField } from "../basic/TextField";
import { CircularProgress, TextField as MUITextField } from "@mui/material";
import Stack from "@mui/material/Stack";
import { styled, useTheme } from "@mui/system";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import debounce from "lodash.debounce";
import "../basic/textfield.css";
import searchParentAndChildInterests from "../screens/queries/searchParentAndChildInterests";

const baseTheme = createTheme();

const StyledMUITextField = styled(MUITextField)(
  ({ theme }) => `
    & .MuiInputBase-root {
        border-radius: 16px;

        &:hover,
        &.Mui-focused {
            border: 0;
        }

        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border: solid 2px #B9DEF3;
        }  

        &.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline {
            border-color: ${baseTheme.palette.error.main}
        }

        &.Mui-error {
            & .MuiOutlinedInput-notchedOutline,
            &:hover .MuiOutlinedInput-notchedOutline {
                border-color: ${baseTheme.palette.error.main}
            }
        }

        & .MuiOutlinedInput-notchedOutline{
            border: solid 2px #B9DEF3;
        }
    
        &:hover .MuiOutlinedInput-notchedOutline{
            border: solid 2px #2d9cdb;
        }
    };      
`
);

export const SelectedInterestsAutocomplete = (props) => {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [inputValue, setInputValue] = React.useState("");
  const [selectedValues, setSelectedValues] = React.useState([]);
  // const [inputValueObj, setInputValueObj] = React.useState(props.cityObj);
  const [allInterests, setAllInterests] = React.useState([]);
  const fetchAbortController = React.useRef(null);
  const timeoutId = React.useRef(null);

  // Fetch cities once when component mounts
  React.useEffect(() => {
    let active = true;

    const fetchInterests = async () => {
      setLoading(true);
      try {
        fetchAbortController.current?.abort(); // Cancel previous fetch
        const controller = new AbortController();
        fetchAbortController.current = controller;

        var apiKey = "da2-22ztnhuamje4jptwonwzfafdi4";

        const response = await fetch(
          "https://3arkzxpkarhoflbprgz4tpruai.appsync-api.us-east-1.amazonaws.com/graphql",
          {
            signal: controller.signal,
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "x-api-key": apiKey,
            },
            body: JSON.stringify({
              query: searchParentAndChildInterests(
                inputValue?.slice(0, 1)?.toUpperCase() + inputValue?.slice(1),
                props?.interestLocationType
              ),
            }),
          }
        );

        if (!response.ok) throw new Error("Failed to load cities");

        const backendResponse = await response.json();
        if (active) {
          const allInterests = backendResponse?.data?.listInterests?.items;
          // console.log("allInterests", allInterests);
          setAllInterests(allInterests);
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error.name !== "AbortError") console.error(error);
      }
    };

    if (inputValue?.length > 0) {
      // Clear previous search invocation if called prior to timeout
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      timeoutId.current = setTimeout(async () => {
        console.log("inputValue", inputValue);
        fetchInterests();
      }, [500]);
    }

    return () => {
      active = false;
      fetchAbortController.current?.abort(); // Cleanup on unmount
    };
  }, [inputValue]);

  // Filter cities based on input (debounced)
  const debouncedFilter = React.useMemo(
    () =>
      debounce((query) => {
        if (!query) return setOptions([]);
        const filtered = allInterests
          .filter(
            (city) =>
              city.title.toLowerCase().includes(query.toLowerCase()) ||
              (city.parentTitle &&
                city?.parentTitle?.toLowerCase()?.includes(query.toLowerCase()))
          )
          .slice(0, 20); // Limit results
        setOptions(filtered);
      }, 300), // 300ms debounce
    [allInterests]
  );

  React.useEffect(() => {
    debouncedFilter(inputValue);
  }, [inputValue, debouncedFilter]);

  const handleSelection = (value, event) => {
    var newValue = event?.target?.attributes?.optionObj?.title
      ? JSON?.parse(event?.target?.attributes?.optionObj?.title)
      : null;

    var newParentTitle = event?.target?.attributes?.optionObj?.parentTitle
      ? JSON?.parse(event?.target?.attributes?.optionObj?.parentTitle)
      : null;

    setInputValue(value);

    // console.log("newValue", newValue, value);

    if (typeof newValue === "object" && newValue !== null) {
      if (Object.keys(newValue)?.length > 0) {
        props.callback(newValue);
      }
    }
  };

  const handleSelections = (event, newValues) => {
    setSelectedValues(newValues);
    props.callback(newValues);
  };

  return (
    <Stack spacing={3}>
      <Autocomplete
        multiple
        id="tags-outlined"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, value) => option?.title === value.title}
        options={options || []}
        getOptionLabel={(option) => `${option?.parentTitle} - ${option?.title}`}
        defaultValue={props.data}
        filterSelectedOptions
        onChange={handleSelections}
        onInputChange={(_, value) => handleSelection(value, _)}
        // renderOption={(props, option) => {
        //     const { key, ...optionProps } = props;
        //     return (
        //       <Box
        //         key={key}
        //         optionObj={JSON.stringify(option)}
        //         component="li"
        //         sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
        //         {...optionProps}
        //       >
        //         {option.parentTitle} ({option.title})
        //       </Box>
        //     )
        // }}
        renderInput={(params) => (
          <StyledMUITextField
            {...params}
            fullWidth
            onChange={(e) => {
              setTimeout(() => {
                setInputValue(e.target.value);
              }, 1000);
            }}
            label={props.placeholder || "Selected interests"}
            placeholder={props.placeholder || "Enter interests"}
            InputProps={{
              ...params.InputProps,
              endAdornment: loading ? (
                <Box display={"flex"}>
                  <CircularProgress size={20} />
                </Box>
              ) : null,
            }}
          />
        )}
      />
    </Stack>
  );
};

export default SelectedInterestsAutocomplete;
